// The trip laid out by day, for the Days view: which day and part of the day each thing lands on,
// whether it's firm (a time, or booked) or fuzzy (a part of the day, or any time that day), where
// people go separate ways, and what has no day yet. Plain data; Days.tsx draws it.
import { dayDate, daySpan, tripStart } from "../../src/days.ts";
import type { Item, Person, TripView, When } from "../../src/web/types.ts";
import { itemKind } from "./format.ts";

export type LaneKey = "any" | "morning" | "afternoon" | "evening" | "night";

export const LANES: { key: LaneKey; label: string }[] = [
  { key: "any", label: "Any time" },
  { key: "morning", label: "Morning" },
  { key: "afternoon", label: "Afternoon" },
  { key: "evening", label: "Evening" },
  { key: "night", label: "Night" },
];

export interface Block {
  item: Item;
  time: string | null;
  firm: boolean;
  detail: string;
  // Who said they're going, by person id (members only).
  going: string[];
}

export interface Group {
  // Set when people go separate ways: who this group is.
  people: string[] | null;
  blocks: Block[];
}

export interface Lane {
  key: LaneKey;
  label: string;
  groups: Group[];
}

export interface Stay {
  item: Item;
  nights: number;
  // The rail runs from the first night to the morning after the last.
  railStart: boolean;
  railEnd: boolean;
  // Sleeping there tonight (not just checking out this morning).
  tonight: boolean;
}

export interface DayRow {
  kind: "day";
  n: number;
  // The day's date, when the trip has dates.
  date: string | null;
  lanes: Lane[];
  stay: Stay | null;
}

export interface GapRow {
  kind: "gap";
  first: number;
  last: number;
  firstDate: string | null;
  lastDate: string | null;
}

export interface Plan {
  rows: (DayRow | GapRow)[];
  // Days are numbered from the trip's first date ("Day 8"); without trip dates they're dates or "day N" alone.
  numbered: boolean;
  // A time but no day ("any morning").
  sometime: Item[];
  // No time at all yet.
  untimed: Item[];
}

const timeOf = (local: string) => local.slice(11, 16);
const hourLane = (local: string): LaneKey => {
  const hour = Number(local.slice(11, 13));
  return hour < 12 ? "morning" : hour < 17 ? "afternoon" : hour < 21 ? "evening" : "night";
};

function placement(when: When | undefined): { lane: LaneKey; time: string | null } {
  if (!when) return { lane: "any", time: null };
  if (when.precision === "exact" || when.precision === "window")
    return { lane: hourLane(when.start), time: timeOf(when.start) };
  if (when.precision === "part") return { lane: when.part, time: null };
  return { lane: "any", time: null };
}

// `undecided`: choices nobody has picked an option for yet, shown on their first day as not decided.
export function plan(trip: TripView, items: Item[], undecided: { choice: Item; options: number }[] = []): Plan {
  const members = new Set(trip.members.map((m) => m.id));
  const live = items.filter(
    (i) => i.status !== "dropped" && i.status !== "cancelled" && ["place", "goal", "stay", "move"].includes(i.type),
  );
  const dated = (i: Item) =>
    i.when && "start" in i.when ? i.when.start.slice(0, 10) : i.when && "day" in i.when ? i.when.day : undefined;

  // Without trip dates, the earliest date anything has stands in as the first day.
  const ownStart = tripStart(trip.dates);
  const firstDate = live
    .map(dated)
    .filter((d): d is string => Boolean(d && /^\d{4}/.test(d)))
    .sort()[0];
  const start = ownStart ?? firstDate ?? null;

  const days = new Map<number, Map<LaneKey, Block[]>>();
  const stays: { item: Item; first: number; last: number }[] = [];
  const sometime: Item[] = [];
  const untimed: Item[] = [];
  const going = (i: Item) => i.interest.filter((x) => x.going === "yes" && members.has(x.person)).map((x) => x.person);

  for (const { choice, options } of undecided) {
    const span = daySpan(choice.when, start);
    if (!span) continue;
    const at = placement(choice.when);
    const nights = span.last > span.first ? `Days ${span.first}–${span.last} · ` : "";
    const lanes = days.get(span.first) ?? new Map<LaneKey, Block[]>();
    const block: Block = {
      item: choice,
      time: at.time,
      firm: false,
      // As Bookings says it: "no options yet" rather than a bare 0.
      detail: `${nights}not decided · ${options === 0 ? "no options yet" : `${options} ${options === 1 ? "option" : "options"}`}`,
      going: [],
    };
    lanes.set(at.lane, [...(lanes.get(at.lane) ?? []), block]);
    days.set(span.first, lanes);
  }

  for (const item of live) {
    const span = daySpan(item.when, start);
    if (!span) {
      (item.when && item.when.precision !== "none" ? sometime : untimed).push(item);
      continue;
    }
    if (item.type === "stay") {
      stays.push({ item, first: span.first, last: span.last });
      continue;
    }
    const at = placement(item.when);
    const many = span.last > span.first;
    for (let n = span.first; n <= span.last; n++) {
      const lane = n === span.first ? at.lane : "any";
      const block: Block = {
        item,
        time: n === span.first ? at.time : null,
        firm: item.when?.precision === "exact" || item.status === "booked" || item.status === "done",
        detail: [
          many ? `Days ${span.first}–${span.last}` : null,
          itemKind(item),
          item.status === "booked" ? "booked" : null,
        ]
          .filter(Boolean)
          .join(" · "),
        going: going(item),
      };
      const lanes = days.get(n) ?? new Map<LaneKey, Block[]>();
      lanes.set(lane, [...(lanes.get(lane) ?? []), block]);
      days.set(n, lanes);
    }
  }

  const spans = [...days.keys(), ...stays.flatMap((s) => [s.first, s.last])];
  const tripSpan = ownStart ? daySpan(trip.dates, ownStart) : null;
  if (tripSpan) spans.push(tripSpan.first, tripSpan.last);
  const rows: (DayRow | GapRow)[] = [];
  if (spans.length > 0) {
    const first = Math.min(...spans);
    const last = Math.max(...spans);
    let gapFrom: number | null = null;
    const closeGap = (to: number) => {
      if (gapFrom === null) return;
      rows.push({
        kind: "gap",
        first: gapFrom,
        last: to,
        firstDate: dayDate(gapFrom, start),
        lastDate: dayDate(to, start),
      });
      gapFrom = null;
    };
    for (let n = first; n <= last; n++) {
      const lanes = days.get(n);
      const stay = stays.find((s) => n >= s.first && n <= s.last);
      if (!lanes && !stay) {
        gapFrom ??= n;
        continue;
      }
      closeGap(n - 1);
      rows.push({
        kind: "day",
        n,
        date: dayDate(n, start),
        lanes: LANES.flatMap(({ key, label }) => {
          const blocks = lanes?.get(key);
          return blocks ? [{ key, label, groups: groups(sortBlocks(blocks), trip.members) }] : [];
        }),
        stay: stay
          ? {
              item: stay.item,
              nights: Math.max(1, stay.last - stay.first),
              railStart: n === stay.first,
              railEnd: n === stay.last,
              tonight: n < stay.last || stay.first === stay.last,
            }
          : null,
      });
    }
    closeGap(last);
  }

  // A goal is placed once one of its candidates is ("kaiseki once" when the Kikunoi dinner has a day).
  const placed = new Set(
    live.filter((i) => i.parent && daySpan(i.when, start) !== null).map((i) => i.parent as string),
  );
  const open = (list: Item[]) => list.filter((i) => !(i.type === "goal" && placed.has(i.id)));
  const wanted = (i: Item) => i.interest.filter((x) => x.tier && x.tier !== "no").length;
  return {
    rows,
    numbered: ownStart !== null,
    sometime: open(sometime),
    untimed: open(untimed).sort((a, b) => wanted(b) - wanted(a) || a.title.localeCompare(b.title)),
  };
}

function sortBlocks(blocks: Block[]): Block[] {
  return [...blocks].sort(
    (a, b) => (a.time ?? "99").localeCompare(b.time ?? "99") || a.item.title.localeCompare(b.item.title),
  );
}

// When everyone in a part of the day is split into groups that don't overlap (Meiwin and Mei here,
// Ben there), each group gets its own column. Otherwise it's one list.
function groups(blocks: Block[], members: Person[]): Group[] {
  const key = (b: Block) => [...b.going].sort().join(",");
  const everyone = members.length;
  const sets = new Map<string, string[]>();
  for (const b of blocks) {
    if (b.going.length === 0 || b.going.length === everyone) return [{ people: null, blocks }];
    sets.set(key(b), b.going);
  }
  const distinct = [...sets.values()];
  const disjoint = distinct.every((a, i) => distinct.every((b, j) => i === j || !a.some((p) => b.includes(p))));
  if (distinct.length < 2 || !disjoint) return [{ people: null, blocks }];
  return distinct.map((people) => ({ people, blocks: blocks.filter((b) => key(b) === [...people].sort().join(",")) }));
}
